import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Autocomplete,
  Alert,
  Typography,
} from "@mui/material";
import { getAdults, createAdult, createAgent, updateAgent } from "./api/agentsApi";

const COMMISSION_TYPES = ["NONE", "NET", "GROSS"];

const emptyNewAdult = { adultName: "", adultSurname: "", adultEmail: "", adultPhone: "" };

// Create an agent (from an existing or new adult) or edit an existing agent's
// commission defaults. On an existing agent the adult is fixed.
export default function AgentFormDialog({ open, onClose, mode, agent, onSaved }) {
  const isEdit = mode === "edit";

  const [adultMode, setAdultMode] = useState("existing"); // existing | new
  const [adults, setAdults] = useState([]);
  const [selectedAdult, setSelectedAdult] = useState(null);
  const [newAdult, setNewAdult] = useState(emptyNewAdult);

  const [commission, setCommission] = useState("GROSS");
  const [commissionRate, setCommissionRate] = useState("20");
  const [englishSpeaking, setEnglishSpeaking] = useState(false);
  const [notes, setNotes] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSaving(false);
    if (isEdit && agent) {
      setCommission(String(agent.commission || "GROSS").toUpperCase());
      setCommissionRate(agent.commissionRate != null ? String(agent.commissionRate) : "0");
      setEnglishSpeaking(!!agent.englishSpeaking);
      setNotes(agent.notes || "");
    } else {
      setAdultMode("existing");
      setSelectedAdult(null);
      setNewAdult(emptyNewAdult);
      setCommission("GROSS");
      setCommissionRate("20");
      setEnglishSpeaking(false);
      setNotes("");
      // Load adults for the picker.
      getAdults()
        .then((data) => setAdults(Array.isArray(data) ? data : []))
        .catch(() => setAdults([]));
    }
  }, [open, isEdit, agent]);

  const rateRequired = commission === "NET" || commission === "GROSS";

  const adultLabel = (a) =>
    `${a.adultName || ""} ${a.adultSurname || ""}`.trim() +
    (a.adultEmail ? ` · ${a.adultEmail}` : "");

  const canSave = useMemo(() => {
    if (rateRequired) {
      const n = Number(commissionRate);
      if (!Number.isFinite(n) || n <= 0) return false;
    }
    if (isEdit) return true;
    if (adultMode === "existing") return !!selectedAdult;
    return newAdult.adultName.trim().length > 0;
  }, [rateRequired, commissionRate, isEdit, adultMode, selectedAdult, newAdult]);

  const agentBody = () => ({
    commission,
    commissionRate: rateRequired ? Number(commissionRate) : 0,
    englishSpeaking,
    notes: notes.trim() || null,
  });

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setError(null);
    try {
      let saved;
      if (isEdit) {
        saved = await updateAgent(agent.id, agentBody());
      } else {
        let adultId = selectedAdult?.id;
        if (adultMode === "new") {
          const created = await createAdult({
            adultName: newAdult.adultName.trim(),
            adultSurname: newAdult.adultSurname.trim(),
            adultEmail: newAdult.adultEmail.trim(),
            adultPhone: newAdult.adultPhone.trim(),
          });
          adultId = created?.id;
        }
        if (!adultId) throw new Error("Could not resolve the adult to attach.");
        saved = await createAgent(adultId, agentBody());
      }
      await onSaved?.(saved);
      onClose?.();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? "Edit agent" : "New agent"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{String(error.message || error)}</Alert> : null}

          {isEdit ? (
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {agent?.name || `Agent #${agent?.id}`}
            </Typography>
          ) : (
            <>
              <RadioGroup
                row
                value={adultMode}
                onChange={(e) => setAdultMode(e.target.value)}
              >
                <FormControlLabel value="existing" control={<Radio />} label="Existing adult" />
                <FormControlLabel value="new" control={<Radio />} label="New adult" />
              </RadioGroup>

              {adultMode === "existing" ? (
                <Autocomplete
                  options={adults}
                  value={selectedAdult}
                  onChange={(_, v) => setSelectedAdult(v)}
                  getOptionLabel={adultLabel}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Find adult" size="small" />
                  )}
                />
              ) : (
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1.5}>
                    <TextField
                      label="First name"
                      size="small"
                      fullWidth
                      required
                      value={newAdult.adultName}
                      onChange={(e) => setNewAdult((p) => ({ ...p, adultName: e.target.value }))}
                    />
                    <TextField
                      label="Surname"
                      size="small"
                      fullWidth
                      value={newAdult.adultSurname}
                      onChange={(e) => setNewAdult((p) => ({ ...p, adultSurname: e.target.value }))}
                    />
                  </Stack>
                  <TextField
                    label="Email"
                    size="small"
                    fullWidth
                    value={newAdult.adultEmail}
                    onChange={(e) => setNewAdult((p) => ({ ...p, adultEmail: e.target.value }))}
                  />
                  <TextField
                    label="Phone"
                    size="small"
                    fullWidth
                    value={newAdult.adultPhone}
                    onChange={(e) => setNewAdult((p) => ({ ...p, adultPhone: e.target.value }))}
                  />
                </Stack>
              )}
            </>
          )}

          <Stack direction="row" spacing={1.5} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="commission-type">Commission</InputLabel>
              <Select
                labelId="commission-type"
                label="Commission"
                value={commission}
                onChange={(e) => setCommission(e.target.value)}
              >
                {COMMISSION_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t === "NONE" ? "None" : t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Rate %"
              size="small"
              type="number"
              sx={{ width: 120 }}
              value={rateRequired ? commissionRate : ""}
              disabled={!rateRequired}
              onChange={(e) => setCommissionRate(e.target.value)}
              inputProps={{ min: 0, step: 0.5 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={englishSpeaking}
                  onChange={(e) => setEnglishSpeaking(e.target.checked)}
                />
              }
              label="English-speaking"
            />
          </Stack>

          <TextField
            label="Notes"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave || saving}>
          {saving ? "Saving…" : isEdit ? "Save" : "Create agent"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
